import MergeIcon from "@mui/icons-material/Merge";
import {
  Alert,
  Box,
  Checkbox,
  DialogContent,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DisplayOnlyField, DisplayOnlyFieldList } from "components/Forms/DisplayOnlyField";
import { MergeStepFooter } from "components/MergePicker/MergeStepFooter";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { DensityUnits, InventoryOverlapResolutionStrategy } from "generated-graphql/graphql";

import { ConflictChoiceSummary, MergeImpact } from "./inventoryOverlaps";
import { MergeImpactSummary } from "./MergeImpactSummary";
import { ReviewFieldGroup } from "./reviewFieldSummaries";

export type ReviewMergeStepProps = {
  impact: MergeImpact;
  /** Chosen field values for the merged chemical, grouped and ordered like the Chemical edit screen. */
  fieldGroups: ReviewFieldGroup[];
  /** One row per overlap: strategy already resolved on step 2. */
  overlapSummaries: {
    facilityId: string;
    facilityName: string;
    reportingYear: number;
    strategy: InventoryOverlapResolutionStrategy;
    keptChemicalName: string | null;
    isFiled: boolean;
    /** Per-field picks made for a Combine resolution; empty for Keep/Blank. */
    conflictChoices: ConflictChoiceSummary[];
  }[];
  /** The merged chemical's density, when at least one overlap combined and a density is set. Rendered once, not per overlap. */
  combineDensity: { density: number; densityUnits: DensityUnits } | null;
  needsExclusionAcknowledgement: boolean;
  acknowledgementReason: string;
  onAcknowledgementReasonChange: (reason: string) => void;
  filedOverlaps: { facilityId: string; facilityName: string; reportingYear: number }[];
  filedYearsAcknowledged: boolean;
  onFiledYearsAcknowledgedChange: (acknowledged: boolean) => void;
  onCancel: () => void;
  onBack: () => void;
  onSave: () => void;
  isMerging: boolean;
  footerStep: { current: number; total: number };
};

const STRATEGY_LABELS: Record<InventoryOverlapResolutionStrategy, string> = {
  [InventoryOverlapResolutionStrategy.Keep]: "Keep one",
  [InventoryOverlapResolutionStrategy.Combine]: "Combine both",
  [InventoryOverlapResolutionStrategy.Blank]: "Leave blank",
};

/**
 * One conflict-choice/density detail row in the overlap section, using the
 * same display-only presentation as the merged-chemical field summaries so
 * the whole step reads as one read-only system.
 */
function LabeledValueRow({
  label,
  value,
  indent = false,
}: {
  label: string;
  value: string;
  indent?: boolean;
}) {
  return (
    <Box sx={indent ? { pl: 3 } : undefined}>
      <DisplayOnlyField label={label} text={value} wrapValue />
    </Box>
  );
}

// The same presentation the app uses for a verified (display-only) Tier II
// report: a small secondary label over plain copyable text, with no input
// chrome at all. A multi-line value renders one line per row and spans the
// full width of its grid row.
function ReadOnlyField({ field }: { field: ReviewFieldGroup["rows"][number][number] }) {
  const lines = field.value.split("\n").filter((line) => line.trim() !== "");
  if (lines.length > 1) {
    return (
      <Box sx={{ gridColumn: "1 / -1" }}>
        <DisplayOnlyFieldList label={field.label} items={lines} />
      </Box>
    );
  }
  return <DisplayOnlyField label={field.label} text={field.value} wrapValue />;
}

// Every row is a two-column grid, like the edit screen's sm={6} pairs. A
// three-field row nests its trailing pair (Density + Density Units) inside
// the second column, so the pair — internal gap included — takes exactly
// half the row, mirroring the edit screen's nested pair. A multiline field
// spans both columns instead — rows never mix a multiline field with a
// second field (see buildReviewFieldSummaries).
function FieldRow({ fields }: { fields: ReviewFieldGroup["rows"][number] }) {
  const [first, ...rest] = fields;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
      {first && <ReadOnlyField field={first} />}
      {rest.length === 2 ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {rest.map((field) => (
            <ReadOnlyField key={field.label} field={field} />
          ))}
        </Box>
      ) : (
        rest.map((field) => <ReadOnlyField key={field.label} field={field} />)
      )}
    </Box>
  );
}

function MergedChemicalSummary({
  fieldGroups,
}: {
  fieldGroups: ReviewMergeStepProps["fieldGroups"];
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Merged chemical</Typography>
      <Stack spacing={3}>
        {fieldGroups.map((group, index) => (
          <Stack key={group.heading ?? `group-${index}`} spacing={1}>
            {group.heading && <Typography variant="subtitle2">{group.heading}</Typography>}
            <Stack spacing={2}>
              {group.rows.map((row, rowIndex) => (
                <FieldRow
                  key={row.map((field) => field.label).join("|") || rowIndex}
                  fields={row}
                />
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function OverlapResolutionsSummary({
  overlapSummaries,
  combineDensity,
}: {
  overlapSummaries: ReviewMergeStepProps["overlapSummaries"];
  combineDensity: ReviewMergeStepProps["combineDensity"];
}) {
  if (overlapSummaries.length === 0) return null;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Inventory overlap resolutions</Typography>
      <Stack spacing={1}>
        {combineDensity && (
          <LabeledValueRow
            label="Combine density"
            value={`${combineDensity.density} ${prettyPrintEnumValue(combineDensity.densityUnits)}`}
          />
        )}
        {overlapSummaries.map((overlap) => (
          <Stack
            // Facility names aren't unique within a tenant, so key on the id.
            key={`${overlap.facilityId}|${overlap.reportingYear}`}
            spacing={1}
            // Extra room above each overlap so its heading line reads as the
            // start of a new block rather than another detail row.
            sx={{ pt: 1 }}
          >
            <Typography variant="subtitle2">
              {overlap.facilityName} — {overlap.reportingYear}: {STRATEGY_LABELS[overlap.strategy]}
              {overlap.keptChemicalName ? ` (${overlap.keptChemicalName})` : ""}
              {overlap.isFiled ? " · filed" : ""}
            </Typography>
            {overlap.conflictChoices.map((choice) => (
              <LabeledValueRow
                key={choice.label}
                label={choice.label}
                value={choice.value}
                indent
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

export function ReviewMergeStep(props: ReviewMergeStepProps) {
  const saveDisabled =
    props.isMerging ||
    (props.needsExclusionAcknowledgement && props.acknowledgementReason.trim().length === 0) ||
    (props.filedOverlaps.length > 0 && !props.filedYearsAcknowledged);

  return (
    <>
      <DialogContent sx={{ height: "80vh", overflowY: "auto" }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <MergeImpactSummary mode="preview" impact={props.impact} />
          <Divider />
          <MergedChemicalSummary fieldGroups={props.fieldGroups} />
          <OverlapResolutionsSummary
            overlapSummaries={props.overlapSummaries}
            combineDensity={props.combineDensity}
          />
          {props.needsExclusionAcknowledgement && (
            <Stack spacing={1}>
              <Alert severity="warning">
                The merged chemical will be excluded from Tier II reporting even though it has
                hazards listed. Please confirm this is intentional and provide a reason.
              </Alert>
              <TextField
                label="Reason"
                value={props.acknowledgementReason}
                onChange={(event) => props.onAcknowledgementReasonChange(event.target.value)}
                multiline
                minRows={2}
                fullWidth
                required
                helperText="Required when hazards are present."
                data-testid="merge-exclusion-acknowledgement-reason"
              />
            </Stack>
          )}
          {props.filedOverlaps.length > 0 && (
            <Stack spacing={1}>
              <Alert severity="warning">
                This merge changes inventory for already-filed reports. The filed report itself will
                not be changed, but the facility's underlying inventory will.
              </Alert>
              <List dense disablePadding data-testid="merge-filed-year-affected-scope">
                {props.filedOverlaps.map((overlap) => (
                  <ListItem key={`${overlap.facilityId}|${overlap.reportingYear}`} disableGutters>
                    <Typography variant="body2">
                      {overlap.facilityName} — {overlap.reportingYear}
                    </Typography>
                  </ListItem>
                ))}
              </List>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.filedYearsAcknowledged}
                    onChange={(event) => props.onFiledYearsAcknowledgedChange(event.target.checked)}
                    data-testid="merge-filed-year-acknowledge"
                  />
                }
                label="I acknowledge this merge changes inventory for already-filed reports."
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <MergeStepFooter
        onCancel={props.onCancel}
        onBack={props.onBack}
        step={props.footerStep}
        primary={{
          content: (
            <>
              <MergeIcon /> CONFIRM MERGE
            </>
          ),
          onClick: props.onSave,
          disabled: saveDisabled,
          loading: props.isMerging,
          testId: "merge-exclusion-acknowledgement-submit",
        }}
      />
    </>
  );
}
